import { useCallback } from 'react'
import { api } from '@/lib/api'
import { Combobox, type ComboboxOption, type ComboboxPage } from './ui/combobox'
import type { CustomerListItem, Paged } from '@/types/api'

/** Enough to choose from without typing, and few enough that typing is the quicker way. */
const MATCHES = 50

/**
 * The customer a quotation is for, searched rather than scrolled.
 *
 * <p>
 * One picker for every place a quotation is addressed to somebody - raising one, and
 * copying one - because both are bound by the same two rules and should not each have a
 * copy of them. Same country only: the letterhead, the currency and the price list are the
 * country's, so another country's customer on this one's quotation is the mistake this
 * product exists to prevent. Active only: a customer marked inactive is one Codemax has
 * stopped taking new work for, and the API refuses a quotation against it - offering it
 * here would be offering something that can only fail.
 * </p>
 *
 * Searched on the server, the way the Customers listing is, and by the same two things:
 * company name and registration number.
 */
export function CustomerPicker({
  id,
  countryId,
  countryName,
  value,
  onChange,
  placeholder = 'Choose a customer',
  current,
}: {
  id?: string
  countryId: string | undefined
  countryName: string | undefined
  value: ComboboxOption | null
  onChange: (customer: ComboboxOption) => void
  placeholder?: string
  /**
   * A customer to point out in the list - the one a quotation being copied was raised for,
   * so it can be found again after somebody has tried another.
   */
  current?: string
}) {
  // Keyed on the country, so a change of it drops whatever was loaded for the last one.
  const search = useCallback(
    async (query: string, signal: AbortSignal): Promise<ComboboxPage> => {
      const found = await api.get<Paged<CustomerListItem>>(
        '/customers',
        { countryId, status: 'Active', search: query, pageSize: MATCHES },
        signal,
      )

      return {
        options: found.items.map((item) => ({
          value: item.id,
          label: item.companyName,
          hint: [item.registrationNo, item.id === current && 'this customer'].filter(Boolean).join(' · ') || undefined,
        })),
        more: found.hasMore,
      }
    },
    [countryId, current],
  )

  const where = countryName ?? 'this country'

  return (
    <Combobox
      id={id}
      value={value}
      onChange={onChange}
      search={search}
      placeholder={placeholder}
      searchPlaceholder="Search by company or registration number"
      empty={(query) =>
        query ? `No active customer in ${where} matches "${query}".` : `No active customers in ${where} yet.`
      }
    />
  )
}
